import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/apiClient.js';
import { notifySessionChanged } from './sessionEvents.js';
import { clearSandboxPass } from '../features/auth/sandboxPass.js';
import { prepareActivityIdentityTransition } from '../lib/activityCapture.js';
import { prepareFeedbackIdentityTransition } from '../features/feedbackMarkup/feedbackIdentityTransition.js';

// Round 3 (R3-H01): fence and persist the outgoing actor's Feedback mode
// session BEFORE the auth cookie changes. When meaningful local-only feedback
// work cannot be stored restorably, the identity mutation must NOT proceed —
// the user is told to save or discard the draft first.
async function prepareIdentityTransitionOrThrow() {
  prepareActivityIdentityTransition();
  const feedbackTransition = await prepareFeedbackIdentityTransition();
  if (feedbackTransition?.blocked) {
    const error = new Error(feedbackTransition.message);
    error.code = 'FEEDBACK_DRAFT_BLOCKS_TRANSITION';
    throw error;
  }
}

const CurrentUserContext = createContext(null);

/**
 * PRIME-ARCH-001 seam 2 — Identity adapter boundary (documentation-as-code).
 *
 * CurrentUserProvider is the SINGLE identity seam for the web app: every
 * consumer reads identity through `useCurrentUser()` / `useOptionalCurrentUser()`
 * and nothing else hydrates `/api/me`. That makes this context the exact plug
 * point where a future embedding host (Bifrost) would inject a TRUSTED PARENT
 * IDENTITY instead of the self-service `/me` bootstrap — without touching any
 * consumer. Authorization stays server-authoritative: this boundary only
 * changes HOW the identity primitives are hydrated, never who is allowed to do
 * what (that remains enforced by the API on every request).
 *
 * This block is the contract; `__tests__/currentUserContract.test.jsx` locks the
 * public shape so it cannot drift silently. Keep all three in sync.
 *
 * Identity primitives (adapter-supplied fields; source today: GET /api/me):
 * @typedef {object} CurrentUserIdentity
 * @property {object|null} user     Authenticated principal (id, displayName, …).
 *                                   Source: `/me`.data.user. Consumers: RootRedirect,
 *                                   RouteGuards, TopBar, MyProfilePage.
 * @property {string[]} roles       Role ids for the principal. Source: `/me`.data.roles.
 *                                   Consumers: RequireRoute, SideNav visibility, RBAC UI.
 * @property {string[]} permissions Flattened permission ids. Source: `/me`.data.permissions.
 * @property {object|null} auth     Auth posture for the session, e.g.
 *                                   `{ authMode, mustChangePassword }`. Source:
 *                                   `/me`.data.auth. Consumers: RootRedirect,
 *                                   change-password gate.
 * @property {string} defaultLanding Post-login landing route. Source:
 *                                   `/me`.data.defaultLanding (default `/home`).
 * @property {number} unreadNotificationCount Live bell-badge count. Source: `/me`
 *                                   then pushed by the notification poller.
 * @property {boolean} loading      True while the identity is hydrating.
 * @property {Error|null} error     Last hydration error, if any.
 *
 * Session/identity actions (behavior unchanged by this ticket):
 * @property {() => Promise<void>} refresh              Re-hydrate identity from `/me`.
 * @property {(persona: string) => Promise<any>} setPersona   Demo persona switch.
 * @property {(creds: {email,password}) => Promise<any>} loginWithPassword
 * @property {() => Promise<any>} loginWithCac
 * @property {(args: {currentPassword,newPassword}) => Promise<any>} changePassword
 * @property {() => Promise<void>} logout
 * @property {(count: number) => void} setUnreadNotificationCount
 * @property {(delta?: number) => void} bumpUnread
 *
 * An embedded adapter would satisfy the identity primitives from the parent and
 * leave the actions to no-op / delegate to the parent shell — but that is
 * FUTURE work: this ticket documents the boundary and does not implement it.
 */
const PUBLIC_STATUS_ROUTE_PATTERN = /^\/status\/[^/]+$/;

function shouldBootstrapCurrentUser(pathname) {
  return !PUBLIC_STATUS_ROUTE_PATTERN.test(pathname || '/');
}

export function CurrentUserProvider({ children }) {
  const bootstrapCurrentUser = shouldBootstrapCurrentUser(window.location.pathname);
  const [state, setState] = useState({
    user: null,
    roles: [],
    permissions: [],
    auth: null,
    loading: bootstrapCurrentUser,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const me = await api.get('/me');
      setState({
        user: me.data?.user || null,
        roles: me.data?.roles || [],
        permissions: me.data?.permissions || [],
        auth: me.data?.auth || null,
        defaultLanding: me.data?.defaultLanding || '/home',
        unreadNotificationCount: me.data?.unreadNotificationCount || 0,
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({
        user: null,
        roles: [],
        permissions: [],
        auth: null,
        loading: false,
        error: err,
      });
    }
  }, []);

  useEffect(() => {
    if (!bootstrapCurrentUser) {
      return;
    }
    refresh();
  }, [bootstrapCurrentUser, refresh]);

  const setPersona = useCallback(
    async (persona) => {
      // Flush and rotate the activity-capture session BEFORE the auth cookie
      // changes: the batch endpoint attributes whole batches from the request
      // context, so anything still queued must go out under the old identity.
      // The feedback identity epoch advances in the same pre-cookie window.
      await prepareIdentityTransitionOrThrow();
      const res = await api.post('/auth/login', { persona });
      // The cookie has been set by the server; refresh `me`.
      await refresh();
      await notifySessionChanged();
      return res?.data;
    },
    [refresh],
  );

  const loginWithPassword = useCallback(
    async ({ email, password }) => {
      await prepareIdentityTransitionOrThrow();
      const res = await api.post('/auth/login', { email, password });
      await refresh();
      await notifySessionChanged();
      return res?.data;
    },
    [refresh],
  );

  const loginWithCac = useCallback(async () => {
    await prepareIdentityTransitionOrThrow();
    const res = await api.post('/auth/login', { method: 'cac' });
    await refresh();
    await notifySessionChanged();
    return res?.data;
  }, [refresh]);

  const changePassword = useCallback(
    async ({ currentPassword, newPassword }) => {
      const res = await api.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      await refresh();
      await notifySessionChanged();
      return res?.data;
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await prepareIdentityTransitionOrThrow();
    await api.post('/auth/logout', {});
    // Drop any stored sandbox/admin routing pass so the next person on this
    // browser cannot inherit the prior user's still-live pass without going
    // back through the front door.
    clearSandboxPass();
    await refresh();
    await notifySessionChanged();
  }, [refresh]);

  // Live-count wiring (Feature G): the notification poller pushes fresh unread
  // counts here so the TopBar bell badge re-renders without a full /me refetch.
  // Stable identities so the poller's listener registration doesn't churn.
  const setUnreadNotificationCount = useCallback((count) => {
    setState((s) => ({ ...s, unreadNotificationCount: Math.max(0, Number(count) || 0) }));
  }, []);
  const bumpUnread = useCallback((delta = 1) => {
    setState((s) => ({
      ...s,
      unreadNotificationCount: Math.max(0, (s.unreadNotificationCount || 0) + delta),
    }));
  }, []);

  return (
    <CurrentUserContext.Provider
      value={{
        ...state,
        refresh,
        setPersona,
        loginWithPassword,
        loginWithCac,
        changePassword,
        logout,
        setUnreadNotificationCount,
        bumpUnread,
      }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
}

export function useCurrentUser() {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) throw new Error('useCurrentUser must be used inside CurrentUserProvider');
  return ctx;
}

// For consumers that can render outside the provider (e.g. capture plumbing
// exercised in isolation by tests): same context, no hard requirement.
export function useOptionalCurrentUser() {
  return useContext(CurrentUserContext);
}
